import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Redirect } from 'react-router-dom';

import { selectAuth, selectLoginUrl } from '../../queries/selectors/user';
import { selectPathname, selectLanguage, selectCountry, selectContentProvider, selectIdentityProvider } from '../../global-selectors';

const mapStatetoProps = (state) => ({
  isAuth: selectAuth(state),
  pathname: selectPathname(state),
  loginURL: selectLoginUrl(state),
  countryCode: selectCountry(state),
  language: selectLanguage(state),
  cp: selectContentProvider(state),
  idp: selectIdentityProvider(state),
});

/**
 * withAuthGuard
 *
 * @description React HOC that checks if the use is authenticated, if is not redirects him to an auth page
 * @param {Object} withAuthParams - HOC Custom params
 * @param {String} [withAuthParams.redirectTo] - Redirect URL when the user is not log in
 * @returns {Function}
 */
const withAuthGuard = (withAuthParams) =>
  /**
   * @param {React.ReactNode} WrappedComponent - Any React Component
   * @param {Function}
   */
  (WrappedComponent) =>
    /**
     * @param {React.Props} props - React props
     * @returns {React.ReactNode}
     */
    (props) => {
      /**
       * @description Redirect the user to a login if his is not authenticated
       * @param {React.Props} props - Components props
       * @param {Boolean} [props.isAuth] isAuth - The discriminatory boolean that checks if the user is auth or not
       * @param {String} [props.pathname] pathname - the current location.pathname where the user is going through
       * @returns {React.ReactNode}
       */
      const Wrapper = ({ isAuth, pathname, loginURL, countryCode, language, cp, idp, ...props }) => {
        const { location } = props;
        const redirectTo = `${withAuthParams.redirectTo}?to=${pathname}`;

        if (!isAuth) {
          if (withAuthParams.toLoginURL) {
            const populatedReturnURL = `${window.location.origin}${location.pathname}${location.search}`;
            const populatedLoginURL = loginURL
              .replace('{CP}', cp)
              .replace('{IDP}', idp)
              .replace('{RETURN_URL}', encodeURIComponent(populatedReturnURL))
              .replace('{COUNTRY_CODE}', countryCode)

            window.location.replace(populatedLoginURL);
            return null;
          }

          return <Redirect to={redirectTo} />;
        }

        return <WrappedComponent {...props} />
      };

      Wrapper.propTypes = {
        isAuth: PropTypes.bool.isRequired,
        pathname: PropTypes.string.isRequired,
        loginURL: PropTypes.string.isRequired,
        language: PropTypes.string.isRequired,
        countryCode: PropTypes.string.isRequired,
        cp: PropTypes.string.isRequired,
        idp: PropTypes.string.isRequired,
        isHref: PropTypes.string.isRequired,
        component: PropTypes.oneOfType([
          PropTypes.node,
          PropTypes.any
        ]).isRequired
      };

      return Wrapper(props);
    };

export default (params) => compose(
  connect(mapStatetoProps),
  withAuthGuard(params)
);
